<template>
  <div class="l-main">
    <div class="c-general-configuration">
      <div class="l-main__ctn">
        <div class="c-cloudflare-navigation">
          <div class="c-cloudflare-navigation-breadcrumbs">
            <ul
              class="c-cloudflare-breadcrumbs"
            >
              <li
                class="c-cloudflare-breadcrumbs--item"
                @click="resetPath()"
              >
                <md-icon>home</md-icon>
              </li>

              <template
                v-for="(path, index) in currentPath"
              >
                <li
                  :key="`separator-${index}`"
                  class="c-cloudflare-breadcrumbs--separator"
                >
                  <md-icon>navigate_next</md-icon>
                </li>
                <li
                  :key="`item-${index}`"
                  class="c-cloudflare-breadcrumbs--item"
                  :class="{'is-current': index === currentPath.length - 1}"
                  @click="handleBreadcrumbsClick(index)"
                >
                  <span>{{ path }}</span>
                </li>
              </template>
            </ul>
          </div>

          <div
            v-if="currentPath.length && canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.CLOUDFLARE.key}`)"
            class="c-cloudflare-navigation-delete"
          >
            <button
              class="c-cloudflare-navigation-delete-button"
              @click="openDeleteFileModal($event, currentPathString, true)"
            >
              <md-icon>delete</md-icon>
              <span>Delete Current Folder</span>
            </button>
          </div>
        </div>

        <md-field
          class="c-search"
          md-clearable
          md-inline
        >
          <md-icon>search</md-icon>
          <label>Search</label>
          <md-input v-model="filterQuery"></md-input>
        </md-field>


        <div
          v-if="loading"
          class="spinner-icon"
        >
          <md-progress-spinner
            :md-diameter="40"
            md-mode="indeterminate"
          />
        </div>

        <div
          class="c-cloudflare-content-wrapper"
        >
          <div
            v-if="!files.length && !loading"
            class="c-cloudflare-empty"
          >
            <md-empty-state
              md-icon="announcement"
              md-label="No files uploaded"
            />
          </div>

          <div
            v-if="!filterQuery"
            class="c-files-container"
          >
            <div class="c-files-list--folder-wrapper">
              <div
                v-if="currentPath.length"
                class="c-files-list--item c-files-list--folder"
                @click="goBackFolder()"
              >
                <md-icon class="c-files-list--folder-icon">
                  drive_folder_upload
                </md-icon>
                <p class="c-files-list--folder-name">
                  Go Back
                </p>
              </div>

              <div
                v-for="folderName in visibleFolders"
                :key="folderName"
                class="c-files-list--item c-files-list--folder"
                @click="handleFolderSelect(folderName)"
              >
                <md-icon class="c-files-list--folder-icon">
                  folder
                </md-icon>
                <p class="c-files-list--folder-name">
                  {{ folderName }}
                </p>
              </div>
            </div>

            <div class="c-files-list--file-wrapper">
              <div
                v-for="file in visibleFilesWithoutFolder"
                :key="file.etag + file.lastModified"
                class="c-files-list--item c-files-list--file"
                @click="openFilePreview(file)"
              >
                <img
                  class="c-files-list--image"
                  :src="cloudflareImagePath + '/' + file.fullPath"
                  :alt="file.key"
                >
                <p class="c-files-list--file-name">
                  {{ file.key }}
                </p>
                <p class="c-files-list--file-size">
                  {{ file.size | fileSize }}
                </p>
                <p class="c-files-list--file-date">
                  {{ file.lastModified | formatDate }}
                </p>
                <button
                  v-if="canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.CLOUDFLARE.key}`)"
                  class="c-files-list--delete"
                  @click="openDeleteFileModal($event, file.fullPath)"
                >
                  <md-icon>delete</md-icon>
                </button>
              </div>
            </div>
          </div>

          <div
            v-else
            class="c-files-container"
          >
            <div class="c-files-list--file-wrapper">
              <div
                v-if="!filteredFiles.length"
                class="c-cloudflare-empty"
              >
                <md-empty-state
                  md-icon="announcement"
                  md-label="There are currently no files for given filter"
                />
              </div>

              <div
                v-for="file in filteredFiles"
                :key="file.etag + file.lastModified"
                class="c-files-list--item c-files-list--file"
                @click="openFilePreview(file)"
              >
                <img
                  class="c-files-list--image"
                  :src="cloudflareImagePath + '/' + file.fullPath"
                  :alt="file.key"
                >
                <p class="c-files-list--file-name">
                  {{ file.key }}
                </p>
                <p class="c-files-list--file-size">
                  {{ file.size | fileSize }}
                </p>
                <p class="c-files-list--file-date">
                  {{ file.lastModified | formatDate }}
                </p>
                <button
                  v-if="canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.CLOUDFLARE.key}`)"
                  class="c-files-list--delete"
                  @click="openDeleteFileModal($event, file.fullPath)"
                >
                  <md-icon>delete</md-icon>
                </button>
              </div>
            </div>
          </div>

          <div
            class="c-cloudflare-file-preview"
            :class="{'open': previewDrawerOpened}"
          >
            <single-cloudflare-image-drawer
              :cloudflare-image="previewImage"
              @close="closeImagePreview"
            />
          </div>

          <div
            v-if="canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.CLOUDFLARE.key}`)"
            class="c-cloudflare-file-upload"
          >
            <generic-button
              variation="green"
              @click="openUploadImageModal()"
            >
              <md-icon>cloud_upload</md-icon>
              Upload new images
            </generic-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import {PERMISSION_NAMES} from "@/const/online-permissions";
import {mapActions} from "vuex";
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import {configurationsService} from "@/services/configurations";
import GenericButton from "@/shared/buttons/GenericButton.vue";
import UploadImageModal from "@/pages/on-line/configurations/components/cloudflare/upload-image-modal/index.vue";
import moment from "moment";
import SingleCloudflareImageDrawer
  from "@/pages/on-line/configurations/components/cloudflare/single-image-modal/index.vue";
import ConfirmationModalComponent from "@/shared/confirmation-modal/ConfirmationModalComponent.vue";

export default {
  name: "CloudflareConfiguration",

  components: {
    SingleCloudflareImageDrawer,
    GenericButton
  },

  filters: {
    formatDate(date) {
      const momentDate = moment(date);
      const now = moment();

      if (momentDate.isSame(now, 'day')) {
        return momentDate.fromNow();
      } else if (momentDate.isAfter(now.subtract(7, 'days'))) {
        return momentDate.fromNow();
      } else {
        return momentDate.format('MMMM D, YYYY');
      }
    },

    fileSize(bytes) {
      if (bytes === 0) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
    },

    date(dateString) {
      return moment(dateString).format('DD/MM/YYYY HH:mm')
    }

  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      loading: false,
      filterQuery: '',
      files: [],
      currentPath: [],
      previewImage: null,
      previewDrawerOpened: false,
      cloudflareImagePath: 'https://img.meridianbet.com/cdn-cgi/image/height=100,quality=50,format=webp',
    }
  },

  computed: {
    PERMISSION_NAMES() {
      return PERMISSION_NAMES
    },

    currentPathString() {
      return this.currentPath.join('/')
    },

    visibleFiles() {
      return this.files
        .filter(file => file.key.startsWith(this.currentPathString))
    },

    visibleFilesWithoutFolder() {
      return this.visibleFiles
        .map(file => {
          const fullPath = file.key;

          let key = file.key;
          if (key.startsWith(this.currentPathString)) {
            key = key.slice(this.currentPathString.length);
          }
          if (key.startsWith('/')) {
            key = key.slice(1);
          }

          return {
            ...file,
            key,
            fullPath,
          }
        })
        .filter(file => !file.key.includes('/'))
        .sort((a, b) => a.key - b.key)
    },

    visibleFolders() {
      return [
        ...new Set(this.visibleFiles
          .map(file => {
            let key = file.key;
            if (key.startsWith(this.currentPathString)) {
              key = key.slice(this.currentPathString.length);
            }
            if (key.startsWith('/')) {
              key = key.slice(1);
            }

            return key
          })
          .filter(file => file.includes('/'))
          .map(file => file.split('/')[0])
        )
      ]
    },

    filteredFiles() {
      return this.files
        .filter(file => file.key.toLowerCase().includes(this.filterQuery.toLowerCase()))
        .map(file => {
          const fullPath = file.key;

          let key = file.key;
          if (key.startsWith(this.currentPathString)) {
            key = key.slice(this.currentPathString.length);
          }
          if (key.startsWith('/')) {
            key = key.slice(1);
          }

          return {
            ...file,
            key,
            fullPath,
          }

        })
    }
  },

  async created() {
    await this.getFiles();
  },

  methods: {
    ...mapActions("response", ["handleSuccess", "setGlobalError"]),

    goToHome() {
      this.$router.push({name: 'home'});
    },

    async getFiles() {
      this.loading = true;
      try {
        const {data} = await configurationsService.getFiles();

        this.files = data;
      } catch (err) {
        this.setGlobalError(err.data.message);
      }

      this.loading = false;
    },

    openFilePreview(file) {
      this.previewImage = file
      this.previewDrawerOpened = true;
    },

    openUploadImageModal() {
      this.$modal.show(
        UploadImageModal,
        {
          currentPathString: this.currentPathString,
          successFunction: this.handleFilesUploaded,
        },
        {height: 'auto', scrollable: true}
      );
    },

    goBackFolder() {
      this.currentPath = this.currentPath.slice(0, this.currentPath.length - 1);
    },

    handleFolderSelect(folderName) {
      this.currentPath = [...this.currentPath, folderName];
    },

    handleBreadcrumbsClick(index) {
      this.currentPath = this.currentPath.slice(0, index + 1);
      this.filterQuery = '';
    },

    resetPath() {
      this.currentPath = [];
      this.filterQuery = '';
    },

    closeImagePreview() {
      this.previewDrawerOpened = false;
      this.previewImage = null;
    },

    async openDeleteFileModal($event, path, isFolder = false) {
      $event.preventDefault();
      $event.stopPropagation();

      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: `Are you sure you want to delete this ${isFolder ? 'folder' : 'file'}?`,
          modalData: {
            path,
            isFolder
          },
          onAccept: this.deleteFile,
          onClose: () => {
          },
        },
        {height: "auto", scrollable: false, width: "400px"}
      );
    },
    async deleteFile(deleteData) {
      try {
        const path = deleteData.isFolder ? deleteData.path + '?folder=true' : deleteData.path
        await configurationsService.deleteFile(path)
        if (deleteData.path === this.previewImage.fullPath) {
          this.closeImagePreview();
        }

        if (deleteData.isFolder) {
          this.goBackFolder();
          this.closeImagePreview();
        }

        await this.getFiles();

        this.handleSuccess({message: 'File deleted successfully'})

      } catch (error) {
        this.setGlobalError(error.data.message)
      }
    },

    async handleFilesUploaded() {
      await this.getFiles();
    }
  },
};
</script>

<style
  lang="scss"
  scoped
>
.l-main__ctn {
  overflow: hidden;
}

.c-general-configuration {
  display: flex;
  flex: 1;
  max-height: 100%;
}

.c-cloudflare-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.c-cloudflare-navigation-breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
  height: 32px;
}

.c-cloudflare-breadcrumbs--home {
  margin-left: 10px;
}

.c-cloudflare-navigation--back {
  flex: 1;
}

.c-cloudflare-navigation-delete-button {
  background: none;
  cursor: pointer;
  color: inherit;

  &:hover {
    cursor: pointer;
    opacity: .7;
  }
}

.c-cloudflare-breadcrumbs {
  display: flex;
  align-items: center;
}

.c-cloudflare-breadcrumbs--item {
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }
}

.c-files-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
}

.c-files-list--item {
  cursor: pointer;
  display: flex;
  position: relative;
  gap: 5px;

  &:hover {
    .c-files-list--delete {
      visibility: visible;
    }
  }
}

.c-files-list--file-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.c-files-list--file {
  height: 24px;
  position: relative;
  display: flex;
  align-items: center;

  &:hover {
    opacity: .7;
  }
}

.c-files-list--image {
  object-fit: scale-down;
  height: 24px;
  width: 30px;
}

.c-files-list--file-name {
  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  flex: 1;
}

.c-files-list--file-size {
  flex: 0 0 auto;
  width: 80px;
}

.c-files-list--file-date {
  flex: 0 0 auto;
  width: 150px;
}

.c-files-list--folder {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 5px;

  &:hover {
    opacity: .6;
  }
}

.c-files-list--folder-icon {
  margin: 0 !important;
}

.c-files-list--folder-name {
  text-overflow: ellipsis;
  text-wrap: nowrap;
  overflow: hidden;
}

.c-files-list--delete {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: none;
  visibility: hidden;

  &:hover {
    .md-icon {
      opacity: .7;
    }
  }

  .md-icon {
    color: red !important;
  }
}

.c-cloudflare-file-upload {
  position: fixed;
  bottom: 30px;
  right: 40px;
}

.c-cloudflare-content-wrapper {
  display: flex;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.c-cloudflare-empty {
  width: 100%;
}

.c-files-container {
  overflow-y: auto;
  flex: 1;
}

.c-cloudflare-file-preview {
  width: 0;
  transition: all .3s;
  overflow: hidden;
  min-height: calc(100% - 60px);
  max-height: calc(100% - 60px);

  &.open {
    transition: all .3s;
    width: 600px !important;
  }
}

.spinner-icon {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus-within {
    border-bottom: 1px solid gray;
  }
}

.c-search {
  width: 400px;
  margin: 0 0 10px;
}

.meridian-theme-dark .md-field.md-theme-default > .md-icon:after {
  background-color: inherit;
}
</style>
